// SB6-ANA-FILT-05 — central hook for the 7-level Global Data Point
// Filter. Owns the URL ↔ Zustand ↔ persisted-prefs bridge, the
// cascade auto-clear, and the per-user persistence.
//
// Selection precedence on entry (D6):
//   1. URL search params, when present (deep-link sharing) — win.
//   2. Else the persisted `LysePreferences.globalDataPointFilter`.
//   3. Else the ACTIVE-only default (`status: ["ACTIVE"]`).
// After hydration the live selection is written through to BOTH the URL
// (replace, so deep-links stay shareable) and the persisted prefs
// (debounced via the shared `useUpdateLysePreferencesMutation`).
//
// Reset to Default (D6): an explicit action restores the ACTIVE-only
// default and persists it; never fires automatically when a data set
// opens.
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import type { DataPointFilterSelection } from "@aeriosiq/types";
import {
  useLysePreferencesQuery,
  useUpdateLysePreferencesMutation,
  type GlobalDataPointFilterPref,
} from "../preferences/index.js";
import {
  defaultSelection,
  selectionsEqual,
  useDataPointFilterStore,
} from "./state/dataPointFilterStore.js";

// URL param names per axis (matches the wire query params).
const URL_KEYS = {
  status: "status",
  reviewState: "reviewState",
  kind: "kind",
  defectType: "defectTypeIds",
  component: "componentIds",
  location: "location",
  severity: "severity",
} as const;

const AXES: (keyof DataPointFilterSelection)[] = [
  "status",
  "reviewState",
  "kind",
  "defectType",
  "component",
  "location",
  "severity",
];

function parseCsv(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** True when at least one filter axis is present in the URL. */
function urlHasFilter(params: URLSearchParams): boolean {
  return AXES.some((axis) => params.get(URL_KEYS[axis]) !== null);
}

function selectionFromUrl(params: URLSearchParams): DataPointFilterSelection {
  return {
    status: parseCsv(params.get(URL_KEYS.status)),
    reviewState: parseCsv(params.get(URL_KEYS.reviewState)),
    kind: parseCsv(params.get(URL_KEYS.kind)),
    defectType: parseCsv(params.get(URL_KEYS.defectType)),
    component: parseCsv(params.get(URL_KEYS.component)),
    location: parseCsv(params.get(URL_KEYS.location)),
    severity: parseCsv(params.get(URL_KEYS.severity)),
  };
}

/** Map the persisted-prefs shape (wire field names) → selection. */
export function selectionFromPref(
  pref: GlobalDataPointFilterPref | null | undefined,
): DataPointFilterSelection {
  if (!pref) return defaultSelection();
  // Each field is `.default([])` in the schema, so they're always present.
  return {
    status: pref.status,
    reviewState: pref.reviewState,
    kind: pref.kind,
    defectType: pref.defectTypeIds,
    component: pref.componentIds,
    location: pref.location,
    severity: pref.severity.map((n: number) => String(n)),
  };
}

/** Map a selection → the persisted-prefs shape (wire field names). */
export function selectionToPref(
  selection: DataPointFilterSelection,
): GlobalDataPointFilterPref {
  return {
    status: [...selection.status],
    reviewState: [...selection.reviewState],
    kind: [...selection.kind],
    defectTypeIds: [...selection.defectType],
    componentIds: [...selection.component],
    location: [...selection.location],
    severity: selection.severity
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n)),
  };
}

export interface UseDataPointFilterResult {
  selection: DataPointFilterSelection;
  hydrated: boolean;
  /** Replace one axis; auto-persists + writes URL. */
  setAxis: (
    axis: keyof DataPointFilterSelection,
    next: readonly string[],
  ) => void;
  /** Replace the whole selection (used by the cascade auto-clear). */
  setSelection: (next: DataPointFilterSelection) => void;
  /** Restore the ACTIVE-only default + persist (explicit reset). */
  resetToDefault: () => void;
  /** True when the live selection differs from the ACTIVE-only default. */
  isDefault: boolean;
}

/**
 * Canonical hook for the 7-level Global Data Point Filter. Five+
 * widgets subscribe through `useDataPointFilterStore` for the live
 * selection; this hook owns hydration + writes.
 */
export function useDataPointFilter(): UseDataPointFilterResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const selection = useDataPointFilterStore((s) => s.selection);
  const hydrated = useDataPointFilterStore((s) => s.hydrated);
  const setSelectionStore = useDataPointFilterStore((s) => s.setSelection);
  const setAxisStore = useDataPointFilterStore((s) => s.setAxis);
  const markHydrated = useDataPointFilterStore((s) => s.markHydrated);

  const { prefs, isPending } = useLysePreferencesQuery();
  const { update } = useUpdateLysePreferencesMutation();

  const writeUrl = useCallback(
    (next: DataPointFilterSelection) => {
      const params = new URLSearchParams(searchParams);
      for (const axis of AXES) {
        const key = URL_KEYS[axis];
        const csv = next[axis].join(",");
        if (csv.length === 0) params.delete(key);
        else params.set(key, csv);
      }
      if (params.toString() !== searchParams.toString()) {
        setSearchParams(params, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );

  // ---- Hydration (once, on first settled prefs) ---------------------
  const didHydrateRef = useRef(false);
  useEffect(() => {
    if (didHydrateRef.current) return;
    // Wait for prefs to settle so the URL-absent path reads the real
    // persisted selection (not the in-flight factory default).
    if (isPending) return;
    didHydrateRef.current = true;
    const initial = urlHasFilter(searchParams)
      ? selectionFromUrl(searchParams)
      : selectionFromPref(prefs.globalDataPointFilter);
    setSelectionStore(initial);
    markHydrated();
    // Mirror the hydrated selection into the URL so a deep-link is
    // shareable from the first render even when it came from prefs.
    writeUrl(initial);
    // Runs once when prefs settle; deps intentionally limited to isPending.
    // (No react-hooks/exhaustive-deps disable needed — the plugin only
    // runs on .tsx files in this repo's eslint config.)
  }, [isPending]);

  const persist = useCallback(
    (next: DataPointFilterSelection) => {
      update({ globalDataPointFilter: selectionToPref(next) });
    },
    [update],
  );

  const commit = useCallback(
    (next: DataPointFilterSelection) => {
      setSelectionStore(next);
      writeUrl(next);
      persist(next);
    },
    [setSelectionStore, writeUrl, persist],
  );

  const setSelection = useCallback(
    (next: DataPointFilterSelection) => {
      commit(next);
    },
    [commit],
  );

  const setAxis = useCallback(
    (axis: keyof DataPointFilterSelection, next: readonly string[]) => {
      const merged: DataPointFilterSelection = {
        ...selection,
        [axis]: [...next],
      };
      // Update the store eagerly via the dedicated axis setter so the
      // subscribe broadcast is minimal, then write URL + persist.
      setAxisStore(axis, next);
      writeUrl(merged);
      persist(merged);
    },
    [selection, setAxisStore, writeUrl, persist],
  );

  const resetToDefault = useCallback(() => {
    const next = defaultSelection();
    commit(next);
  }, [commit]);

  const isDefault = useMemo(
    () => selectionsEqual(selection, defaultSelection()),
    [selection],
  );

  return {
    selection,
    hydrated,
    setAxis,
    setSelection,
    resetToDefault,
    isDefault,
  };
}
